// Golf Match Play Tracker 2026 -- Playoff bracket
//
// =====================================================================
// STRUCTURE (16 players: top 4 from each of the 4 divisions)
//
//   Oct 17  Champions Pointe
//     Round of 16   9:30 / 9:40 / 9:50 / 10:00   4 foursomes = 8 matches
//     Quarterfinals 12:30 / 12:40                2 foursomes = 4 matches
//
//   Oct 18  Covered Bridge
//     Semifinals    10:00 / 10:10   2 twosomes = 2 matches  (FRONT 9)
//     Championship  12:30           1 twosome  = 1 match    (BACK 9)
//
// The auto-seed below is a SUGGESTION. Every matchup is editable in the
// Playoffs tab before the bracket is locked -- nothing here is forced.
// =====================================================================

import { uid, teeTimeSlots } from "./helpers";
import { DIVISIONS } from "./constants";
import { BRACKET_ROUND_LABELS } from "../data/seedSchedule";

export { BRACKET_ROUND_LABELS };

export const BRACKET_SHAPE = [
  { round: 1, matches: 8, playersPerGroup: 4, label: "Round of 16" },
  { round: 2, matches: 4, playersPerGroup: 4, label: "Quarterfinals" },
  { round: 3, matches: 2, playersPerGroup: 2, label: "Semifinals" },
  { round: 4, matches: 1, playersPerGroup: 2, label: "Championship" },
];

/**
 * Suggested Round of 16: within each division, 1 vs 4 and 2 vs 3.
 * Produces 8 matches. Order alternates divisions so the bracket halves
 * are not all one division.
 */
export function suggestRoundOf16(qualifiers) {
  const matchups = [];
  DIVISIONS.forEach(division => {
    const q = qualifiers[division] || [];
    if (q.length < 4) return;
    matchups.push({ division, a: q[0].golfer.id, b: q[3].golfer.id, label: `${division} 1 v 4` });
    matchups.push({ division, a: q[1].golfer.id, b: q[2].golfer.id, label: `${division} 2 v 3` });
  });
  return matchups;
}

/**
 * Suggested cross-division seeding alternative: rank all 16 qualifiers by
 * points and pair 1v16, 2v15, ... Offered as a one-click option in the UI.
 */
export function suggestCrossDivision(qualifiers) {
  const all = DIVISIONS.flatMap(d => qualifiers[d] || [])
    .slice()
    .sort((a, b) =>
      b.points - a.points || b.wins - a.wins || b.winTiePoints - a.winTiePoints);

  const matchups = [];
  for (let i = 0; i < Math.floor(all.length / 2); i++) {
    const hi = all[i];
    const lo = all[all.length - 1 - i];
    matchups.push({
      division: null,
      a: hi.golfer.id,
      b: lo.golfer.id,
      label: `Seed ${i + 1} v Seed ${all.length - i}`,
    });
  }
  return matchups;
}

/**
 * Turn a list of Round-of-16 matchups into playoff match rows, and create
 * the empty shell matches for later rounds so the bracket is visible and
 * editable from the start.
 *
 * @param matchups  [{ a, b, label }] length 8
 * @param seasonDates  the two playoff season_dates rows (with .assignments)
 */
export function buildBracket(matchups, seasonDates) {
  const playoffDates = (seasonDates || [])
    .filter(d => d.phase === "playoff")
    .slice()
    .sort((a, b) => a.seq - b.seq);

  // Map bracket_round -> the assignment that hosts it
  const hosts = {};
  playoffDates.forEach(d => {
    (d.assignments || []).forEach(asg => {
      if (asg.bracket_round) hosts[asg.bracket_round] = { date: d, asg };
    });
  });

  const matches = [];
  const warnings = [];

  BRACKET_SHAPE.forEach(({ round, matches: count, playersPerGroup }) => {
    const host = hosts[round];
    if (!host) {
      warnings.push(`No date/tee times configured for ${BRACKET_ROUND_LABELS[round]}.`);
      return;
    }
    const { date, asg } = host;
    const slots = teeTimeSlots(asg.first_tee_time, asg.interval_min || 10, asg.slot_count || 1);
    const matchesPerSlot = playersPerGroup / 2;   // 4-ball = 2 matches, 2-ball = 1
    const slotsNeeded = Math.ceil(count / matchesPerSlot);

    if (slotsNeeded > slots.length) {
      warnings.push(
        `${BRACKET_ROUND_LABELS[round]}: needs ${slotsNeeded} tee times, ${slots.length} configured.`
      );
    }

    for (let slotIndex = 0; slotIndex < slotsNeeded; slotIndex++) {
      const teeTime = slots[Math.min(slotIndex, slots.length - 1)];
      const slotMatchups = [];
      for (let k = 0; k < matchesPerSlot; k++) {
        const idx = slotIndex * matchesPerSlot + k;
        if (idx >= count) break;
        slotMatchups.push(round === 1 ? (matchups[idx] || null) : null);
      }

      const flatPlayers = [];
      slotMatchups.forEach(mu => {
        flatPlayers.push(mu?.a || "", mu?.b || "");
      });

      matches.push({
        id: uid("p"),
        season_date_id: date.id,
        match_date: date.play_date,
        tee_time: teeTime,
        course_id: asg.course_id,
        nine: asg.nine || "F9",
        round_no: null,
        phase: "playoff",
        bracket_round: round,
        bracket_slot: slotIndex + 1,
        group_players: flatPlayers.filter(Boolean),
        pairings: flatPlayers,          // "" = TBD, filled by the winner of a prior round
        results: Object.fromEntries(flatPlayers.filter(Boolean).map(id => [id, "pending"])),
        golfer_selections: {},
        locked: false,
        notes: slotMatchups.map(m => m?.label).filter(Boolean).join(" | ") || null,
      });
    }
  });

  return { matches, warnings };
}

/**
 * Winners of a bracket round, in bracket order, so the next round can be
 * populated. A forfeit advances the opponent. A tie must be resolved
 * (extra holes) before the winner can be recorded.
 */
export function roundWinners(playoffMatches, round) {
  const inRound = playoffMatches
    .filter(m => m.bracket_round === round)
    .slice()
    .sort((a, b) => (a.bracket_slot || 0) - (b.bracket_slot || 0) ||
                    String(a.tee_time).localeCompare(String(b.tee_time)));

  const winners = [];
  const pending = [];

  inRound.forEach(m => {
    for (let i = 0; i + 1 < m.pairings.length; i += 2) {
      const a = m.pairings[i];
      const b = m.pairings[i + 1];
      if (!a || !b) { winners.push(null); pending.push({ match: m, reason: "matchup not set" }); continue; }
      const ra = m.results?.[a] || "pending";
      const rb = m.results?.[b] || "pending";

      if (ra === "win" || rb === "forfeit") winners.push(a);
      else if (rb === "win" || ra === "forfeit") winners.push(b);
      else {
        winners.push(null);
        pending.push({
          match: m,
          reason: ra === "tie" || rb === "tie"
            ? "match halved -- playoff matches must have a winner (extra holes)"
            : "result not entered",
        });
      }
    }
  });

  return { winners, pending, complete: winners.every(Boolean) && winners.length > 0 };
}

/** Push the winners of `round` into the matchups of `round + 1`. */
export function advanceRound(playoffMatches, round) {
  const { winners } = roundWinners(playoffMatches, round);
  const nextShape = BRACKET_SHAPE.find(s => s.round === round + 1);
  if (!nextShape) return { updated: [], note: "Championship is the final round." };

  const matchesPerSlot = nextShape.playersPerGroup / 2;
  const next = playoffMatches
    .filter(m => m.bracket_round === round + 1)
    .slice()
    .sort((a, b) => (a.bracket_slot || 0) - (b.bracket_slot || 0));

  const updated = [];
  next.forEach((m, slotIndex) => {
    const flat = [];
    for (let k = 0; k < matchesPerSlot; k++) {
      const idx = (slotIndex * matchesPerSlot + k) * 2;
      flat.push(winners[idx] || "", winners[idx + 1] || "");
    }
    const players = flat.filter(Boolean);
    updated.push({
      ...m,
      pairings: flat,
      group_players: players,
      results: {
        ...Object.fromEntries(players.map(id => [id, m.results?.[id] || "pending"])),
      },
    });
  });

  return { updated, note: null };
}
